/** @jsx jsx */
import type { SerializedStyles } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Theme } from '@coursera/cds-core';
import type { ProductType } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import type { CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import ProductCard from 'bundles/product-card/components/legacy/ProductCard';
import { getAriaLabel } from 'bundles/product-card/utils/productCardUtils';

import { eventingV3ProductType, extractEventId } from '../utils/productCardV2Utils';

const styles = {
  /* TrackedLink2 adds an extra div that interferes with the card styling.
    This wrapper div applies style to that extra div to ensure the cards are styled correctly. */
  targetCardWrapper: () => css`
    height: 100%;
    width: 100%;

    > * {
      height: inherit;
      width: inherit;
    }

    a {
      display: block;
      height: inherit;
      overflow: hidden;
      border-radius: 8px;
      text-decoration: none !important;
      background-color: var(--cds-color-neutral-background-primary);
      -webkit-appearance: listitem;
      border: 2px solid var(--cds-color-grey-50);

      :hover,
      :focus {
        box-shadow: 4px 5px 10px 8px var(--cds-color-grey-50);
        outline: none;
      }
    }
  `,
};

type Props = {
  entity: CollectionEntity;
  entityIndex: number;
  trackingData?: Record<string, unknown>;
  trackingName?: string;
  customStyles?: SerializedStyles | ((theme: Theme) => SerializedStyles);
  cardHeader?: React.ReactNode;
  productCardImageProps?: { height?: number; width?: number; fit?: string };
  truncatePartnerName?: boolean;
  carouselIndex: number;
};

const ProductCardContainer = ({
  entity,
  entityIndex,
  trackingName,
  trackingData,
  carouselIndex,
  customStyles,
  cardHeader,
  productCardImageProps,
  truncatePartnerName,
}: Props) => {
  const track = useTracker();
  const { id, name, slug, productType } = entity;
  const collectionName = trackingData?.collectionName as string;
  const collectionId = trackingData?.collectionId as string;

  const eventingV3TrackingData = {
    productCard: {
      index: entityIndex ? entityIndex + 1 : 1,
    },
    product: {
      id: extractEventId(id),
      name: name ?? '',
      slug: slug as string,
      type: eventingV3ProductType(productType) as ProductType,
    },
    carousel: {
      name: collectionName,
      id: collectionId || (collectionName ?? ''),
      section: carouselIndex,
    },
  };

  const handleClick = () => {
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    track('click_product_card', eventingV3TrackingData);
  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClick();
    }
  };

  return (
    <div css={[styles.targetCardWrapper, customStyles]} key={entity.id}>
      <TrackedLink2
        type="button"
        trackingName={trackingName || 'product_card'}
        data={trackingData}
        href={entity.link}
        withVisibilityTracking={true}
        tabIndex={0}
        ariaLabel={getAriaLabel(entity)}
        onKeyDown={(e) => handleKeyDown(e)}
        onClick={handleClick}
      >
        <ProductCard
          cardProps={entity}
          cardPosition={entityIndex}
          ownsCourseraPlus={false}
          ownsCourseraLite={false}
          cardHeader={cardHeader}
          productCardImageProps={productCardImageProps}
          truncatePartnerName={truncatePartnerName}
          // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          eventingV3TrackingData={eventingV3TrackingData}
          slug={slug}
        />
      </TrackedLink2>
    </div>
  );
};

export default ProductCardContainer;
